
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Season } from '@/types/season';

export const useSeasonManagement = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);
  const [seasonYear, setSeasonYear] = useState('');
  const [seasonName, setSeasonName] = useState('');
  const [selectedClubId, setSelectedClubId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSeasons = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('seasons')
        .select(`
          *,
          clubs!seasons_club_id_fkey(name)
        `)
        .order('season_year', { ascending: false });


      if (error) {
        console.error('Error fetching seasons:', error);
        toast.error(`Failed to load seasons: ${error.message}`);
        return;
      }
      
      setSeasons(data || []);
    } catch (error) {
      console.error('Error fetching seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeasons();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seasonYear || !seasonName || !selectedClubId) return;

    setIsSubmitting(true);
    try {
      const seasonData = {
        season_year: parseInt(seasonYear),
        name: seasonName,
        club_id: selectedClubId
      };


      if (editingSeason) {
        const { error } = await supabase
          .from('seasons')
          .update(seasonData)
          .eq('id', editingSeason.id);

        if (error) throw error;
        toast.success('Season updated successfully');
      } else {
        const { error } = await supabase
          .from('seasons')
          .insert([seasonData]);

        if (error) throw error;
        toast.success('Season created successfully');
      }

      handleDialogClose();
      fetchSeasons();
    } catch (error) {
      console.error('Error saving season:', error);
      toast.error(`Failed to save season: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (season: Season) => {
    setEditingSeason(season);
    setSeasonYear(season.season_year.toString());
    setSeasonName(season.name);
    setSelectedClubId(season.club_id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (seasonId: string) => {
    if (!confirm('Are you sure you want to delete this season?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('seasons')
        .delete()
        .eq('id', seasonId);

      if (error) throw error;
      toast.success('Season deleted successfully');
      fetchSeasons();
    } catch (error) {
      console.error('Error deleting season:', error);
      toast.error('Failed to delete season');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingSeason(null);
    setSeasonYear('');
    setSeasonName('');
    setSelectedClubId('');
  };

  return {
    seasons,
    loading,
    isDialogOpen,
    setIsDialogOpen,
    editingSeason,
    seasonYear,
    setSeasonYear,
    seasonName,
    setSeasonName,
    selectedClubId,
    setSelectedClubId,
    isSubmitting,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleDialogClose,
  };
};
